/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import { useState } from 'react';
import { api } from "../api";
import { Search, FileText, CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronUp, X, FileCheck, Download } from "lucide-react";
import toast from 'react-hot-toast';

const isImageUrl = (url) => /\.(jpe?g|png|gif|webp)$/i.test(url);
const isPdfUrl   = (url) => /\.pdf$/i.test(url);

const SecureGateLookup = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState({});
  const [approvedDocs, setApprovedDocs] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showRawText, setShowRawText] = useState({});

  // ============================================================
  // FLUXONIX BRANDING (inline styles)
  // ============================================================

  const colors = {
    primary: "#5B4BFF",
    primaryDark: "#4338CA",
    secondary: "#8B5CF6",
    gradient: "linear-gradient(135deg, #5B4BFF, #8B5CF6)",
    bgSoft: "#F7F7FC",
    textDark: "#111827",
    textSoft: "#6B7280",
    success: "#22C55E",
    warning: "#F59E0B",
  };

  const fx = {
    page: {
      minHeight: "100vh",
      padding: "50px 20px",
      background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
      fontFamily: "Inter, sans-serif",
    },
    card: {
      maxWidth: "950px",
      margin: "0 auto",
      background: "#fff",
      padding: "45px",
      borderRadius: "24px",
      boxShadow: "0 15px 40px rgba(80, 70, 200, 0.15)",
    },
    h1: {
      fontSize: "32px",
      fontWeight: 800,
      color: colors.textDark,
      marginBottom: "6px",
    },
    subtitle: {
      fontSize: "16px",
      color: colors.textSoft,
      marginBottom: "35px",
    },
    searchRow: {
      display: "flex",
      gap: "12px",
      marginBottom: "30px",
    },
    input: {
      flex: 1,
      padding: "14px 16px",
      fontSize: "15px",
      borderRadius: "12px",
      border: "2px solid #E5E7EB",
      outline: "none",
      transition: "0.2s",
      boxSizing: "border-box",
    },
    button: {
      padding: "14px 28px",
      fontSize: "15px",
      fontWeight: 700,
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      color: "#fff",
      background: colors.gradient,
      boxShadow: "0 8px 20px rgba(91,75,255,0.35)",
      transition: "0.25s",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      whiteSpace: "nowrap",
    },
    buttonDisabled: {
      padding: "14px 28px",
      fontSize: "15px",
      fontWeight: 700,
      borderRadius: "12px",
      border: "none",
      cursor: "not-allowed",
      color: "#fff",
      background: "#CBD5E1",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      whiteSpace: "nowrap",
    },
    resultCard: {
      background: "#fff",
      borderRadius: "16px",
      padding: "24px",
      marginBottom: "20px",
      border: "1px solid #E5E7EB",
      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    },
    resultHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    resultTitle: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "18px",
      fontWeight: 700,
      color: colors.textDark,
    },
    chip: (status) => ({
      padding: "6px 14px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: 600,
      background: status === "approved" ? "#DCFCE7" : "#FEF3C7",
      color: status === "approved" ? "#166534" : "#92400E",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    }),
    metaText: {
      fontSize: "14px",
      color: colors.textSoft,
      marginBottom: "16px",
    },
    divider: {
      height: "1px",
      background: "#E5E7EB",
      margin: "16px 0",
    },
    imagePreview: {
      maxWidth: "100%",
      maxHeight: "200px",
      objectFit: "contain",
      cursor: "pointer",
      borderRadius: "12px",
      border: "1px solid #E5E7EB",
    },
    checklistBox: {
      background: colors.bgSoft,
      padding: "20px",
      borderRadius: "14px",
      marginBottom: "16px",
      borderLeft: `4px solid ${colors.secondary}`,
    },
    checklistTitle: {
      fontSize: "15px",
      fontWeight: 700,
      color: colors.textDark,
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    checklistItem: {
      fontSize: "14px",
      color: colors.textDark,
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "8px",
    },
    checklistChip: (status) => ({
      padding: "2px 10px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: 600,
      background: status === "passed" ? "#DCFCE7" : "#FEF3C7",
      color: status === "passed" ? "#166534" : "#92400E",
    }),
    rawTextToggle: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "14px",
      fontWeight: 600,
      color: colors.primary,
      padding: "8px 0",
    },
    rawTextBox: {
      background: colors.bgSoft,
      padding: "16px",
      borderRadius: "12px",
      fontSize: "14px",
      fontFamily: "monospace",
      color: colors.textDark,
      whiteSpace: "pre-wrap",
      marginTop: "12px",
      maxHeight: "200px",
      overflowY: "auto",
    },
    approvalInfo: {
      fontSize: "13px",
      color: colors.textSoft,
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    actionRow: {
      display: "flex",
      gap: "12px",
      marginTop: "16px",
    },
    approveBtn: {
      padding: "12px 24px",
      borderRadius: "12px",
      border: "none",
      background: colors.gradient,
      color: "#fff",
      fontWeight: 700,
      fontSize: "14px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      boxShadow: "0 4px 12px rgba(91,75,255,0.25)",
    },
    approveBtnDisabled: {
      padding: "12px 24px",
      borderRadius: "12px",
      border: "none",
      background: "#CBD5E1",
      color: "#fff",
      fontWeight: 700,
      fontSize: "14px",
      cursor: "not-allowed",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    approveBtnSuccess: {
      padding: "12px 24px",
      borderRadius: "12px",
      border: "none",
      background: colors.success,
      color: "#fff",
      fontWeight: 700,
      fontSize: "14px",
      cursor: "default",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    pdfBtn: {
      padding: "12px 24px",
      borderRadius: "12px",
      border: `2px solid ${colors.success}`,
      background: "transparent",
      color: colors.success,
      fontWeight: 700,
      fontSize: "14px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.85)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "20px",
    },
    modalContent: {
      position: "relative",
      maxWidth: "90%",
      maxHeight: "90%",
    },
    modalClose: {
      position: "absolute",
      top: "-40px",
      right: "0",
      background: "rgba(255,255,255,0.2)",
      border: "none",
      borderRadius: "50%",
      width: "36px",
      height: "36px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#fff",
    },
    modalImage: {
      maxWidth: "100%",
      maxHeight: "80vh",
      objectFit: "contain",
      borderRadius: "12px",
    },
  };

  // ============================================================
  // API HANDLERS
  // ============================================================

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await api.post('/api/secure/search', { query });
      setResults(res.data);
    } catch (err) {
      console.error('Search failed:', err);
      toast.error("Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGuardApprove = async (docId) => {
    setApproving(prev => ({ ...prev, [docId]: true }));
    try {
      await api.post(`/api/secure/approve-doc/${docId}`);
      toast.success("Approved for Gate Entry!");

      setApprovedDocs(prev => ({ ...prev, [docId]: true }));
      setResults(prev =>
        prev.map(r =>
          r.id === docId
            ? { ...r, status: "approved", workflow_stage: "approved_for_gate_entry" }
            : r
        )
      );
    } catch (err) {
      console.error("Approval failed", err);
      toast.error("Approval failed. Try again.");
    } finally {
      setApproving(prev => ({ ...prev, [docId]: false }));
    }
  };

  const handleGeneratePdf = async (docId) => {
    try {
      const res = await api.get(`/api/secure/documents/${docId}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error("Failed to generate PDF", err);
      toast.error("Could not generate PDF.");
    }
  };

  const toggleRaw = (id) => {
    setShowRawText(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const canSearch = query.trim() !== "";

  // ============================================================
  // RENDER UI
  // ============================================================

  return (
    <div style={fx.page}>
      <div style={fx.card}>
        <h1 style={fx.h1}>PO Document Lookup</h1>
        <p style={fx.subtitle}>
          Search and approve Purchase Order documents for gate entry.
        </p>

        {/* Search Row */}
        <div style={fx.searchRow}>
          <input
            type="text"
            placeholder="Enter PO Number or Query..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canSearch && !loading && handleSearch()}
            style={fx.input}
          />
          <button
            style={canSearch && !loading ? fx.button : fx.buttonDisabled}
            onClick={handleSearch}
            disabled={!canSearch || loading}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search size={18} />
                Search
              </>
            )}
          </button>
        </div>

        {/* Results */}
        {results.map((r, idx) => {
          const isApproved = r.workflow_stage === "approved_for_gate_entry";
          const statusType = isApproved ? "approved" : "pending";

          // --- Unified AI checklist extraction ---
          const newChecklist = r.agent_annotations?.gate_checklist;
          const oldChecklist = r.stage_metadata?.checklist;

          let unifiedChecklist = [];

          if (Array.isArray(newChecklist)) {
            unifiedChecklist = newChecklist; 
          } else if (oldChecklist && typeof oldChecklist === "object") {
            unifiedChecklist = Object.entries(oldChecklist).map(([key, val]) => ({
              label: key.replace(/_/g, " "),
              value: val?.value,
              status: val?.status,
              reason: val?.reason,
            }));
          }

          return (
            <div key={r.id || idx} style={fx.resultCard}>
              {/* Header */}
              <div style={fx.resultHeader}>
                <div style={fx.resultTitle}>
                  <FileText size={22} color={colors.primary} />
                  {r.document_type || "Unknown Type"}
                </div>
                <div style={fx.chip(statusType)}>
                  {isApproved ? (
                    <>
                      <CheckCircle size={14} />
                      Approved
                    </>
                  ) : (
                    <>
                      <AlertCircle size={14} />
                      Pending ({r.workflow_stage})
                    </>
                  )}
                </div>
              </div>

              <div style={fx.metaText}>
                Source: {r.source_type || "upload"} | Score: {r.score?.toFixed(2)}
              </div>

              <div style={fx.divider} />

              {/* Preview image or PDF */}
              {r.file_url && (
                <div style={{ marginBottom: "16px" }}>
                  {isImageUrl(r.file_url) && (
                    <>
                      <img
                        src={r.file_url}
                        alt="Document Preview"
                        onClick={() => setPreviewUrl(r.file_url)}
                        style={fx.imagePreview}
                      />
                      <div style={{ fontSize: "12px", color: colors.textSoft, marginTop: "6px" }}>
                        Click to enlarge
                      </div>
                    </>
                  )}

                  {isPdfUrl(r.file_url) && (
                    <>
                      <embed
                        src={r.file_url}
                        type="application/pdf"
                        style={{
                          width: "100%",
                          height: "300px",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                        }}
                      />
                      {/* <div
                        style={{
                          fontSize: "12px",
                          color: colors.textSoft,
                          marginTop: "6px",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => setPreviewUrl(r.file_url)}
                      >
                        Open PDF in full view
                      </div> */}
                    </>
                  )}
                </div>
              )}

              {/* Unified AI Checklist */}
              {unifiedChecklist.length > 0 && (
                <div style={fx.checklistBox}>
                  <div style={fx.checklistTitle}>
                    <FileCheck size={18} color={colors.secondary} />
                    AI Checklist
                  </div>

                  {unifiedChecklist.map((item, idx) => (
                    <div key={idx} style={fx.checklistItem}>
                      <strong>{item.label}:</strong>
                      <span>{item.value || <i>Missing</i>}</span>
                      <span
                        style={fx.checklistChip(
                          item.status === "passed" || item.status === "✓"
                            ? "passed"
                            : "warning"
                        )}
                      >
                        {item.status}
                      </span>
                      {item.reason && (
                        <span style={{ fontSize: "12px", color: colors.textSoft }}>
                          ({item.reason})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Raw text collapsible */}
              <button style={fx.rawTextToggle} onClick={() => toggleRaw(r.id)}>
                {showRawText[r.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {showRawText[r.id] ? "Hide Raw Text" : "Show Raw Text"}
              </button>
              {showRawText[r.id] && (
                <div style={fx.rawTextBox}>
                  {r.raw_text || "No raw text available."}
                </div>
              )}

              {/* Approval info */}
              {r.stage_metadata?.approved_by && (
                <div style={fx.approvalInfo}>
                  <CheckCircle size={14} color={colors.success} />
                  Approved by {r.stage_metadata.approved_by} at{" "}
                  {new Date(r.stage_metadata.timestamp).toLocaleString()}
                </div>
              )}

              {/* Action buttons */}
              <div style={fx.actionRow}>
                {isApproved ? (
                  <button style={fx.approveBtnSuccess} disabled>
                    <CheckCircle size={16} />
                    Approved
                  </button>
                ) : (
                  <button
                    style={approving[r.id] ? fx.approveBtnDisabled : fx.approveBtn}
                    onClick={() => handleGuardApprove(r.id)}
                    disabled={approving[r.id]}
                  >
                    {approving[r.id] ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Approve for Gate Entry
                      </>
                    )}
                  </button>
                )}

                {(isApproved || approvedDocs[r.id]) && (
                  <button style={fx.pdfBtn} onClick={() => handleGeneratePdf(r.id)}>
                    <Download size={16} />
                    Generate PDF
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Image Modal */}
      {previewUrl && (
        <div style={fx.modal} onClick={() => setPreviewUrl(null)}>
          <div style={fx.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={fx.modalClose} onClick={() => setPreviewUrl(null)}>
              <X size={20} />
            </button>
            <img src={previewUrl} alt="Full Preview" style={fx.modalImage} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SecureGateLookup;